/**
 * Geometria. Onde fracao vira ponto, e so aqui.
 *
 * SISTEMA DE COORDENADAS
 *
 * As primitivas usam origem no canto SUPERIOR ESQUERDO, com `y` crescendo para
 * BAIXO — o mesmo modelo do CSS e do SVG, que e como o editor pensa.
 *
 * O PDF usa o contrario: origem embaixo, `y` crescendo para cima. Essa
 * inversao acontece **dentro do pintor de PDF**, numa linha, e nao vaza para
 * ca. O motivo de escolher o modelo do editor e que todo o resto do sistema
 * (config, arrasto, preview) ja raciocina assim; inverter aqui obrigaria a
 * traduzir de cabeca em cada revisao de codigo.
 *
 * ANCORA
 *
 * `x` e o ponto de ancoragem conforme o alinhamento: borda esquerda em `left`,
 * centro em `center`, borda direita em `right`. `y` e o CENTRO VERTICAL do
 * bloco de texto — nao o topo, nao a baseline.
 *
 * E o mesmo modelo do editor web, onde o elemento é posicionado com
 * `translate(-50%, -50%)`. Mudar isso deslocaria todos os 7 certificados de
 * producao.
 */

import type { Pt, PageSize } from '../../types.js';
import type { TextAlign } from '../../schema/v2.js';
import { FONT_METRICS, type FontId } from '../../fonts/metrics.js';

/**
 * Quantiza a 3 casas decimais.
 *
 * Aplicado a TODA coordenada que entra numa primitiva. Sem isso, o ultimo bit
 * de um float propaga para o fingerprint e o golden test oscila sem que nada
 * de real tenha mudado. Tres casas em pontos e ~0,001pt: quatro ordens de
 * grandeza abaixo do que qualquer impressora resolve.
 */
export function q(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Fracao 0..1 -> ponto absoluto, quantizado. A unica conversao do sistema. */
export function toPt(fracao: number, dimensaoPt: number): Pt {
  return q(fracao * dimensaoPt);
}

/** Proporcao entre altura de linha e corpo. Mesmo valor do renderizador atual. */
export const LINE_HEIGHT_RATIO = 1.35;

/** Corpo em px do canvas web -> pontos. 96dpi para 72dpi. */
export const PX_PARA_PT = 72 / 96;

export function pxParaPt(px: number): Pt {
  return q(px * PX_PARA_PT);
}

export function alturaLinha(tamanhoPt: number): Pt {
  return q(tamanhoPt * LINE_HEIGHT_RATIO);
}

/**
 * Distancia do topo da caixa de linha ate a baseline.
 *
 * Usa o ascender real da fonte, nao a aproximacao de 0,8 que o renderizador
 * atual aplica. Com o ascender verdadeiro, texto em Inter e em Sora — que tem
 * `unitsPerEm` diferentes, 2048 e 1000 — assentam na mesma linha optica.
 */
export function offsetBaseline(font: FontId, tamanhoPt: number): Pt {
  const m = FONT_METRICS[font];
  const alturaGlifos = ((m.ascender - m.descender) * tamanhoPt) / m.unitsPerEm;
  const sobra = alturaLinha(tamanhoPt) - alturaGlifos;
  return q(sobra / 2 + (m.ascender * tamanhoPt) / m.unitsPerEm);
}

/** X de desenho a partir da ancora e do alinhamento. */
export function ancoraX(xPt: Pt, larguraPt: number, align: TextAlign): Pt {
  if (align === 'center') return q(xPt - larguraPt / 2);
  if (align === 'right') return q(xPt - larguraPt);
  return q(xPt);
}

export interface LinhaPosicionada {
  readonly texto: string;
  readonly x: Pt;
  /** Baseline. */
  readonly y: Pt;
}

/**
 * Posiciona um bloco de linhas centrado verticalmente em `yPt`.
 *
 * Recebe as larguras ja medidas para nao remedir: quem chamou precisou delas
 * para quebrar o texto, e medir duas vezes e a porta de entrada para os dois
 * resultados discordarem.
 */
export function posicionarBloco(
  linhas: readonly string[],
  larguras: readonly number[],
  xPt: Pt,
  yPt: Pt,
  align: TextAlign,
  font: FontId,
  tamanhoPt: number,
  page?: PageSize,
): LinhaPosicionada[] {
  if (linhas.length === 0) return [];

  const lh = alturaLinha(tamanhoPt);
  const base = offsetBaseline(font, tamanhoPt);
  let topo = yPt - (linhas.length * lh) / 2;

  /**
   * Puxa o bloco de volta para dentro da pagina, se ele saiu.
   *
   * Clampar a POSICAO a 0..1 nao basta: a 100% a ancora fica na borda, e a
   * baseline do texto cai alguns pontos abaixo dela. O elemento fica ligado,
   * gera primitiva, passa em toda a contagem — e nao aparece em lugar nenhum.
   * Um elemento habilitado que renderiza fora do papel e o mesmo defeito que
   * este pipeline existe para eliminar, so que silencioso.
   *
   * O deslocamento e do bloco inteiro, por um delta unico: clampar linha a
   * linha empilharia todas na borda, trocando "invisivel" por "ilegivel".
   *
   * Quem chamou ja emitiu `VALUE_CLAMPED` ao trazer a posicao para 0..1.
   */
  if (page) {
    const alturaBloco = linhas.length * lh;
    if (topo + alturaBloco > page.height) topo = page.height - alturaBloco;
    if (topo < 0) topo = 0;
  }

  return linhas.map((texto, i) => ({
    texto,
    x: ancoraX(xPt, larguras[i] ?? 0, align),
    y: q(topo + i * lh + base),
  }));
}

/**
 * Largura util de um elemento.
 *
 * `width` nulo significa "ajusta ao conteudo" — mas o texto ainda precisa de
 * um teto, senao um nome muito longo atravessa a pagina. O teto e a pagina
 * menos uma margem, e nao a pagina inteira, para o texto nao encostar na borda.
 */
export const MARGEM_PAGINA_PT = 40;

export function larguraUtil(
  widthFracao: number | null,
  page: PageSize,
): Pt {
  if (widthFracao === null) return q(page.width - MARGEM_PAGINA_PT * 2);
  return q(Math.min(widthFracao * page.width, page.width - MARGEM_PAGINA_PT));
}

/**
 * Distribui n colunas em torno de um centro.
 *
 * Usado pelas assinaturas. O renderizador atual soma 130pt fixos por
 * signatario a partir da ancora, o que empurra tudo para a direita e faz o
 * ultimo sair da pagina com 4 ou mais assinaturas. Aqui as colunas ficam
 * centradas na ancora, sempre.
 */
export function colunasCentradas(
  centroPt: Pt,
  n: number,
  larguraColunaPt: number,
): Pt[] {
  if (n <= 0) return [];
  const larguraTotal = n * larguraColunaPt;
  const inicio = centroPt - larguraTotal / 2;
  return Array.from({ length: n }, (_, i) =>
    q(inicio + i * larguraColunaPt + larguraColunaPt / 2),
  );
}

/**
 * Distribui n blocos de larguras diferentes em sequencia, centrados.
 *
 * Usado pela linha de dados do evento (Data · Local · Carga horaria). O
 * renderizador atual usa 120pt fixos por coluna: com nome de local longo, o
 * texto de uma coluna invade a seguinte. Aqui cada bloco ocupa a largura que
 * mediu, mais um respiro, entao nunca ha sobreposicao.
 */
export function sequenciaCentrada(
  centroPt: Pt,
  largurasPt: readonly number[],
  espacoPt: number,
): { centro: Pt; inicio: Pt; fim: Pt }[] {
  if (largurasPt.length === 0) return [];

  const total =
    largurasPt.reduce((s, w) => s + w, 0) + espacoPt * (largurasPt.length - 1);

  let cursor = centroPt - total / 2;
  return largurasPt.map((w) => {
    const inicio = cursor;
    cursor += w + espacoPt;
    return { centro: q(inicio + w / 2), inicio: q(inicio), fim: q(inicio + w) };
  });
}
